import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {useEffect, useState} from 'react';
import useLoadAds from '@jobseeker/common-core/hooks/useLoadAds';
import competitionService from '@jobseeker/common-core/services/competition.service';
import {TrackEventUBA} from '@jobseeker/common-core/utils';

import {ButtonV2} from '@jobseeker/lotus/elements';
import {Image} from '@jobseeker/lotus/components/Image';
import {useInView, useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {useSelector} from 'react-redux';
import {EPOCH} from '@constants';

import type {AdsElementType, AppState} from '@jobseeker/common-core/types';
import type {ProgessApiResponse} from './types';
import dayjs from 'dayjs';
import {formQueryParams} from '@utils';
import {isCSR} from '@jobseeker/lotus/utils';

const CompetitionBanner = () => {
  const {
    timelines: {
      ads,
      registration: {endDate},
      round2: {resultDeclarationDate},
      config: {competitionName, competitionId},
    },
  } = useSelector((state: AppState) => state.competition);

  const slotId = ads?.competitionBanner?.slotId ?? '';

  const [bannerInfo, setBannerInfo] = useState(Array<AdsElementType>);
  const [progressInfo, setProgressInfo] = useState<ProgessApiResponse | null>(
    null
  );

  useLoadAds(slotId, setBannerInfo, {}, false);
  const isAboveMobile = useIsLargeDevice('sm');
  const page = useSelector((state: AppState) => state.app.page);
  const {userId, isLoggedIn} = useSelector((state: AppState) => state.auth);
  const currentDate = dayjs();
  const registrationEnded = dayjs(endDate) < currentDate;
  const resultsAnnounced = dayjs(resultDeclarationDate) < currentDate;
  const {competitionIdentity, testSlug} = useSelector(
    (state: AppState) => state.user?.profile
  );

  const registeredForCompetition = competitionIdentity && testSlug;
  const certificatePresent = Boolean(progressInfo?.certificate);

  let queryParams = '';
  if (isCSR()) {
    queryParams = formQueryParams('');
  }

  useEffect(() => {
    const getProgressInfo = async () => {
      const progressInfoData = await competitionService.fetchProgressInfo(
        competitionId
      );
      setProgressInfo(progressInfoData);
    };
    if (userId !== '-1' && isLoggedIn) {
      getProgressInfo();
    }
  }, [userId, isLoggedIn]);

  const handleDownloadCertificate = () => {
    if (!progressInfo?.certificate) {
      return;
    }
    const a = document.createElement('a');
    a.href = progressInfo.certificate;
    a.download = `${competitionName}-certificate.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const isLandingPage = page === EPOCH;

  const handleCtaClick = () => {
    if (!isLandingPage) return;
    TrackEventUBA('marketingPageClick', 'click', {
      pageName: 'EPOCH Landing',
      initiativeName: `EPOCH${new Date().getFullYear()}`,
      actionSrc: registrationEnded ? 'Results Announced' : 'Register Now',
    });
  };

  const {ref: bannerRef, isInView} = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  useEffect(() => {
    if (!isLandingPage || !isInView) return;

    TrackEventUBA('marketingPageView', 'view', {
      pageName: 'EPOCH Landing',
      initiativeName: `EPOCH${new Date().getFullYear()}`,
      actionSrc: 'Banner',
    });
  }, [isLandingPage, isInView]);

  return (
    <>
      <Box
        id="competition-banner"
        ref={bannerRef}
        sx={{
          width: '100%',
          height: {xs: '450px', md: '530px'},
          background: `url(${
            isAboveMobile ? bannerInfo[0]?.companyLogo : bannerInfo[0]?.imageUrl
          }) no-repeat`,
          backgroundColor: '#fff',
          display: 'flex',
          justifyContent: 'center',
          position: 'sticky',
          borderBottomLeftRadius: 36,
          borderBottomRightRadius: 36,
          backgroundSize: {xs: '100% 100%', md: '100% 100%', sm: '100% 100%'},
        }}
      ></Box>
      {page === EPOCH && !registeredForCompetition && (
        <Box
          sx={{
            textAlign: 'center',
            position: 'relative',
            bottom: {xs: 140, md: 150, sm: 180},
          }}
        >
          <ButtonV2
            type="submit"
            sx={{
              width: {
                xxs: '70%',
                sm: 220,
                md: 220,
              },
              height: {xs: '60px', md: '70px'},
              padding: 3,
              borderRadius: 9,
              background: registrationEnded
                ? resultsAnnounced
                  ? '#49966E'
                  : '#ed2048'
                : '#fff',
              ':hover': {
                backgroundColor: registrationEnded
                  ? resultsAnnounced
                    ? '#33694D'
                    : '#ed2048'
                  : '#f0f0f0',
              },
              color: registrationEnded ? '#fff' : 'primary.main',
              mt: {xxs: 2, md: 1},
            }}
            href={
              registrationEnded
                ? resultsAnnounced
                  ? `/${competitionName}/winners`
                  : 'javascript:void(0);'
                : `/${competitionName}/register${
                    queryParams ? `?${queryParams}` : ''
                  }`
            }
            target="_self"
            variant="contained"
            size="large"
            onClick={
              registrationEnded && !resultsAnnounced
                ? undefined
                : handleCtaClick
            }
          >
            {registrationEnded
              ? resultsAnnounced
                ? 'Results Announced'
                : 'Registration is closed'
              : 'Register Now'}
            {!registrationEnded && (
              <Image
                src={
                  'https://static.iimjobs.com/epoch/static/assets/greenArrow.svg'
                }
                alt={'Arrow'}
                width={18}
                height={22}
                loading="eager"
                style={{
                  margin: '5px 10px',
                  float: 'right',
                }}
              />
            )}
          </ButtonV2>
        </Box>
      )}
      {page === EPOCH &&
        registeredForCompetition &&
        resultsAnnounced &&
        certificatePresent && (
          <Box
            sx={{
              textAlign: 'center',
              position: 'relative',
              bottom: {xs: 140, md: 150, sm: 180},
            }}
          >
            <ButtonV2
              type="button"
              sx={{
                width: {
                  // xxs: '70%',
                  xxs: 260,
                  md: 260,
                },
                height: {xs: '60px', md: '70px'},
                padding: 3,
                borderRadius: 9,
                background: '#fff',
                ':hover': {
                  backgroundColor: '#f0f0f0',
                },
                color: 'primary.main',
                mt: {xxs: 2, md: 1},
              }}
              onClick={handleDownloadCertificate}
              variant="contained"
              size="large"
            >
              Download Certificate
              <Image
                src={
                  'https://static.iimjobs.com/epoch/static/assets/greenArrow.svg'
                }
                alt={'Arrow'}
                width={18}
                height={22}
                loading="eager"
                style={{
                  margin: '5px 10px',
                  float: 'right',
                }}
              />
            </ButtonV2>
            <Typography
              sx={{
                mt: 2,
                mx: 1,
                fontSize: {xs: '12px', md: '16px'},
                color: '#fff',
              }}
            >
              Your Participation Certificate is ready to download.
            </Typography>
          </Box>
        )}
    </>
  );
};

export default CompetitionBanner;
